import React, { useEffect, useMemo, useState } from "react";
import Joi from "joi-browser";
import { Modal } from "react-bootstrap";
import geo from "../../Services/geoService";
import { getAlert, getConfirmAlert } from "../../Services/appAlerts";
import useForm from "../../app/hooks/useForm";
import useControls from "../../app/hooks/useControls";
import { useSelector } from "react-redux";
import {
  getPartRoleTypes,
  getPartUserAddedStatus,
} from "../../app/store/partner";
import {
  useLoadPartRoleTypesQuery,
  useAddPartnerUserMutation,
} from "../../app/store/partnerApiSlice";

const ADD_PARTNER_USER_STYLES = {
  modalContent: {
    borderRadius: "26px",
    overflow: "hidden",
    border: "1px solid rgba(226, 232, 240, 1)",
    boxShadow: "0 24px 60px rgba(15, 23, 42, 0.16)"
  },
  modalDialog: {
    maxWidth: "920px"
  },
  modalHeader: {
    padding: "18px 22px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    background: "linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%)"
  },
  headerContent: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "14px"
  },
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "14px"
  },
  headerIcon: {
    width: "46px",
    height: "46px",
    borderRadius: "15px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(37, 99, 235, 0.12) 0%, rgba(79, 70, 229, 0.16) 100%)",
    color: "#4338ca",
    fontSize: "21px",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)"
  },
  eyebrow: {
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#4f46e5",
    marginBottom: "4px"
  },
  title: {
    fontSize: "22px",
    fontWeight: 800,
    color: "#0f172a",
    lineHeight: 1.1,
    margin: 0
  },
  subtitle: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "4px"
  },
  closeButton: {
    width: "38px",
    height: "38px",
    borderRadius: "999px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    color: "#64748b",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: 800,
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
    flexShrink: 0
  },
  modalBody: {
    padding: "20px 22px",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)"
  },
  helperCard: {
    borderRadius: "16px",
    padding: "16px",
    background: "linear-gradient(135deg, rgba(248,250,252,0.98) 0%, rgba(239,246,255,0.9) 100%)",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    marginBottom: "16px"
  },
  helperTitle: {
    fontSize: "14px",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "8px"
  },
  helperText: {
    fontSize: "12px",
    lineHeight: 1.7,
    color: "#64748b",
    margin: 0
  },
  formCard: {
    borderRadius: "20px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    boxShadow: "0 12px 26px rgba(15, 23, 42, 0.05)",
    padding: "18px 18px 6px 18px"
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "6px"
  },
  sectionSubtitle: {
    fontSize: "12px",
    color: "#64748b",
    marginBottom: "14px",
    lineHeight: 1.6
  },
  modalFooter: {
    padding: "16px 22px 20px 22px",
    borderTop: "1px solid rgba(226, 232, 240, 0.9)",
    background: "#ffffff"
  },
  footerActions: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px"
  },
  cancelButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "999px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    color: "#475569",
    padding: "10px 14px",
    fontSize: "12px",
    fontWeight: 800
  },
  saveButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "999px",
    border: "none",
    background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
    color: "#ffffff",
    padding: "10px 16px",
    fontSize: "12px",
    fontWeight: 800,
    boxShadow: "0 14px 28px rgba(79, 70, 229, 0.2)"
  },
  saveButtonDisabled: {
    opacity: 0.55,
    boxShadow: "none"
  }
};

const AddPartnerUser = (props) => {
  const { showHide, handleModalShowHide, selectedObj, userDetails } = props;

  const [usStateList, setUsStateList] = useState([]);

  const { data: partRoleTypes } = useLoadPartRoleTypesQuery("Partner User Role", {
    skip: !showHide,
  });
  const [addPartnerUserMut] = useAddPartnerUserMutation();

  const partRoleTypesSelector = useSelector(getPartRoleTypes);
  const partUserAddedStatusSelector = useSelector(getPartUserAddedStatus);

  useEffect(() => {
    const loadUsStates = async () => {
      const allStates = await geo.getStateList();
      setUsStateList(allStates);
    };
    loadUsStates();
  }, []);

  const partUserTypes = useMemo(() => {
    return partRoleTypes || partRoleTypesSelector || [];
  }, [partRoleTypes, partRoleTypesSelector]);

  const schema = {
    name: Joi.string().required().label("Name"),
    email: Joi.string().email().required().label("Email"),
    contact: Joi.string()
      .length(10)
      .options({
        language: {
          string: {
            length: "Mobile no must be 10 digits.",
          },
        },
      })
      .options({})
      .regex(/^[0-9]+$/)
      .options({
        language: {
          string: {
            regex: {
              base: "This entry can only contain numbers",
            },
          },
        },
      })
      .label("Mobile No."),
  };

  const [isSaving, setIsSaving] = useState(false);

  const {
    handleChange,
    stateData,
    setStateData,
    stateErrors,
    setStateErrors,
    handleSubmit,
    validate,
  } = useForm(schema, doSubmit);

  const selectedObjId = selectedObj?.id;

  useEffect(() => {
    if (selectedObj) {
      setStateData(selectedObj);
    } else {
      setStateData({});
      setStateErrors({});
    }
  }, [selectedObj, setStateData, setStateErrors]);

  async function doSubmit() {
    const updStateData = { ...stateData };
    updStateData.partnerId = userDetails?.partnerid;
    const result = await getConfirmAlert(
      "Are you sure?",
      `You want to ${selectedObjId > 0 ? "Update" : "Add"} this User`,
      "warning"
    );
    if (result.isConfirmed) {
      setIsSaving(true);
      try {
        await addPartnerUserMut(updStateData).unwrap();
        if (selectedObjId > 0) getAlert("", "User Updated Successfully", "success");
        else getAlert("", "User Added Successfully", "success");
        handleModalShowHide("save");
        setStateData({});
      } catch (err) {
        console.error("Failed to add partner user:", err);
      } finally {
        setIsSaving(false);
      }
    }
  }

  useEffect(() => {
    if (partUserAddedStatusSelector) {
      if (selectedObjId > 0) getAlert("", "User Updated Successfully", "success");
      else getAlert("", "User Added Successfully", "success");

      handleModalShowHide("save");
    }
  }, [partUserAddedStatusSelector, selectedObjId, handleModalShowHide]);

  const { renderSelect, renderInput } = useControls(
    stateData,
    stateErrors,
    handleChange,
    schema
  );

  return (
    <Modal
      show={showHide}
      size="xl"
      centered
      onHide={handleModalShowHide}
      dialogClassName="add-partner-user-modal-dialog"
      contentClassName="add-partner-user-modal-content"
    >
      <style>
        {`
          .add-partner-user-modal-dialog {
            max-width: ${ADD_PARTNER_USER_STYLES.modalDialog.maxWidth};
          }
          .add-partner-user-modal-content {
            border-radius: ${ADD_PARTNER_USER_STYLES.modalContent.borderRadius};
            overflow: ${ADD_PARTNER_USER_STYLES.modalContent.overflow};
            border: ${ADD_PARTNER_USER_STYLES.modalContent.border};
            box-shadow: ${ADD_PARTNER_USER_STYLES.modalContent.boxShadow};
          }
        `}
      </style>
      <Modal.Header style={ADD_PARTNER_USER_STYLES.modalHeader}>
        <div style={ADD_PARTNER_USER_STYLES.headerContent}>
          <Modal.Title as="div" style={ADD_PARTNER_USER_STYLES.titleWrap}>
            <div style={ADD_PARTNER_USER_STYLES.headerIcon}>
              <i className="fa fa-users" aria-hidden="true"></i>
            </div>
            <div>
              <div style={ADD_PARTNER_USER_STYLES.title}>
                {selectedObj?.id > 0 ? "Edit Key Contact / User" : "Add Key Contact / User"}
              </div>
              <div style={ADD_PARTNER_USER_STYLES.subtitle}>
                Capture the contact details and role information for this partner user.
              </div>
            </div>
          </Modal.Title>
          <button
            type="button"
            style={ADD_PARTNER_USER_STYLES.closeButton}
            onClick={handleModalShowHide}
            aria-label="Close add partner user popup"
          >
            <i className="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
      </Modal.Header>
      <Modal.Body style={ADD_PARTNER_USER_STYLES.modalBody}>
        <div style={ADD_PARTNER_USER_STYLES.helperCard}>
          <div style={ADD_PARTNER_USER_STYLES.helperTitle}>Contact setup</div>
          <p style={ADD_PARTNER_USER_STYLES.helperText}>
            Add the user's role, name, contact details, and location so they can be managed
            consistently inside the partner workspace.
          </p>
        </div>

        <div style={ADD_PARTNER_USER_STYLES.formCard}>
          <div style={ADD_PARTNER_USER_STYLES.sectionTitle}>User details</div>
          <div style={ADD_PARTNER_USER_STYLES.sectionSubtitle}>
            Complete the fields below to create or update a partner contact record.
          </div>

          <div className="row">
            <div className="col-lg-12">
              {renderSelect(
                "title",
                "Select User Type",
                partUserTypes,
                "name",
                "name"
              )}
            </div>
            <div className="col-md-12">
              {renderInput("title", "Title")}
            </div>
            <div className="col-lg-6">{renderInput("name", "Name")}</div>
            <div className="col-lg-6">{renderInput("lastName", "Last Name")}</div>
            <div className="col-lg-6">{renderInput("email", "Email")}</div>
            <div className="col-lg-6">
              {renderInput("contact", "Mobile No.", "phone")}
            </div>
            <div className="col-lg-6">
              {renderSelect("state", "State", usStateList, "name", "name")}
            </div>
            <div className="col-lg-6">
              {renderInput("county", "City")}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer style={ADD_PARTNER_USER_STYLES.modalFooter}>
        <div style={ADD_PARTNER_USER_STYLES.footerActions}>
          <button
            type="button"
            style={ADD_PARTNER_USER_STYLES.cancelButton}
            onClick={handleModalShowHide}
          >
            <i className="fa fa-times" aria-hidden="true"></i>
            Cancel
          </button>
          <button
            type="button"
            style={{
              ...ADD_PARTNER_USER_STYLES.saveButton,
              ...(validate() || isSaving ? ADD_PARTNER_USER_STYLES.saveButtonDisabled : {})
            }}
            disabled={validate() || isSaving}
            onClick={handleSubmit}
          >
            {isSaving ? (
              <><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true"></i> {selectedObj?.id > 0 ? "Updating User..." : "Saving User..."}</>
            ) : (
              <><i className="fa fa-check" aria-hidden="true"></i> {selectedObj?.id > 0 ? "Update User" : "Save User"}</>
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddPartnerUser;
